import { historyAccess, historyFamilyLabel, historyCategoryLabel } from '../../../shared/historyScope';
import type { LearningSummary, UserExamRestrictions } from '../utils/api';
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { api } from '../utils/api';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Users, ChevronLeft, ChevronRight, Search, ArrowDown, ArrowUp } from 'lucide-react';
import { getCscaSubjectName } from '../utils/cscaTestPresentation';
import { isAdminRole } from '../utils/permissions';

interface AdminUser {
  id: string;
  username: string;
  email: string;
  test_count: number;
  learning?: { restrictions: UserExamRestrictions; summaries: LearningSummary[] };
  learningError?: boolean;
  avg_score: number | null;
  composite_score?: number | null;
  teacher_access_approved?: boolean;
  assigned_teacher_id?: string | null;
  assigned_teacher_username?: string | null;
}

interface TeacherOption {
  id: string;
  username: string;
  email: string;
}

const AdminPanel: React.FC = () => {
  const { token, user: currentUser } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useSearchParams();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [teachers, setTeachers] = useState<TeacherOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const [teacherLoadError, setTeacherLoadError] = useState('');
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const page = Math.max(1, Math.floor(Number(query.get('page')) || 1));
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const search = query.get('search') || '';
  const [searchInput, setSearchInput] = useState(search);
  const familyFilter = (['2.0', '3.0', 'CSCA'].includes(query.get('family') || '') ? query.get('family') : 'all') as 'all' | '2.0' | '3.0' | 'CSCA';
  const testsFilter = (['has', 'none'].includes(query.get('tests') || '') ? query.get('tests') : 'all') as 'all' | 'has' | 'none';
  const teacherFilter = query.get('teacher') || 'all';
  const accessFilter = (['approved', 'pending'].includes(query.get('access') || '') ? query.get('access') : 'all') as 'all' | 'approved' | 'pending';
  const sortBy = (['name', 'joined'].includes(query.get('sort') || '') ? query.get('sort') : 'tests') as 'tests' | 'name' | 'joined';
  const direction = query.get('direction') === 'asc' ? 'asc' : 'desc';
  const updateQuery = (values: Record<string, string>, resetPage = true) => {
    const next = new URLSearchParams(query);
    if (resetPage) next.set('page', '1');
    Object.entries(values).forEach(([key, value]) => { if (value) next.set(key, value); else next.delete(key); });
    setQuery(next, { replace: true });
  };
  const setPage = (value: number | ((current: number) => number)) => updateQuery({ page: String(typeof value === 'function' ? value(page) : value) }, false);
  useEffect(() => { setSearchInput(search); }, [search]);
  const [reloadKey, setReloadKey] = useState(0);
  const isAdmin = isAdminRole(currentUser?.role);
  const pageSize = 5;

  useEffect(() => {
    let active = true;
    const loadUsers = async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        setError('');

        const response = await api.getAdminUsers(token, {
          page, limit: pageSize, search, family: familyFilter, tests: testsFilter,
          teacher: isAdmin ? teacherFilter : 'all', access: isAdmin ? accessFilter : 'all',
          sort: sortBy, direction
        });
        if (!active) return;
        if (response.success) {
          const maximumPage = Math.max(1, response.pagination?.total_pages || 1);
          if (page > maximumPage) { setPage(maximumPage); return; }
          const enriched = await Promise.all(response.users.map(async entry => {
            try { return { ...entry, learning: await api.getStudentLearningOverview(entry.id, token) }; }
            catch { return { ...entry, learningError: true }; }
          }));
          if (!active) return;
          setUsers(enriched);
          setTotal(response.pagination?.total || 0);
          setTotalPages(maximumPage);
        } else {
          setError('Failed to load users: ' + (response as any).message);
        }
      } catch (err: any) {
        if (!active) return;
        console.error('Admin load error:', err);
        setError(`Failed to load student list: ${err.message}`);
      } finally {
        if (active) { setLoading(false); setHasLoaded(true); }
      }
    };

    loadUsers();
    return () => { active = false; };
  }, [token, page, search, familyFilter, testsFilter, teacherFilter, accessFilter, sortBy, direction, isAdmin, reloadKey]);

  useEffect(() => {
    if (!token || !isAdmin) return;
    const loadTeachers = async () => {
      setTeacherLoadError('');
      try {
        const response = await api.getAdminTeachers(token);
        if (response.success) setTeachers(response.teachers);
        else setTeacherLoadError('Failed to load teacher list.');
      } catch (teacherErr: any) {
        console.error('Teacher list load error:', teacherErr);
        setTeachers([]);
        setTeacherLoadError(teacherErr?.message ? `Failed to load teacher list: ${teacherErr.message}` : 'Failed to load teacher list.');
      }
    };
    loadTeachers();
  }, [token, isAdmin]);

  const setTeacherAccess = async (userId: string, approved: boolean) => {
    if (!token || !isAdmin) return;
    try {
      setSavingUserId(userId);
      await api.setAdminUserTeacherAccess(userId, approved, token);
      setUsers((prev) => prev.map((entry) => entry.id === userId ? { ...entry, teacher_access_approved: approved } : entry));
      setReloadKey((value) => value + 1);
    } catch (err: any) {
      alert(err?.message || 'Failed to update teacher access');
    } finally {
      setSavingUserId(null);
    }
  };

  const setTeacherAssignment = async (userId: string, teacherUserId: string | null) => {
    if (!token || !isAdmin) return;
    try {
      setSavingUserId(userId);
      await api.setAdminUserTeacherAssignment(userId, teacherUserId, token);
      const assignedTeacher = teachers.find((teacher) => teacher.id === teacherUserId) || null;
      setUsers((prev) => prev.map((entry) => entry.id === userId ? {
        ...entry,
        assigned_teacher_id: teacherUserId,
        assigned_teacher_username: assignedTeacher?.username || null
      } : entry));
      setReloadKey((value) => value + 1);
    } catch (err: any) {
      alert(err?.message || 'Failed to assign teacher');
    } finally {
      setSavingUserId(null);
    }
  };

  if (loading && !hasLoaded) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading student data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-primary-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Student Performance</h1>
              <p className="text-gray-600">按学生查看已开放的学习体系与最近成绩</p>
              <Link to="/admin/csca-placement" className="mt-3 inline-flex rounded-lg bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100">CSCA Test · 批改与分班</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Student List</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                按姓名或邮箱搜索，再用筛选与排序定位学生。
              </p>
            </div>
            <form
              className="flex w-full max-w-md gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                updateQuery({ search: searchInput.trim() });
              }}
            >
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  aria-label="搜索学生姓名或邮箱"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search students"
                  className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button type="submit" className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
                Search
              </button>
            </form>
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-gray-100 pt-4 text-sm">
            <label className="flex flex-col gap-1 text-gray-600">
              <span>学习体系</span>
              <select aria-label="筛选学习体系" value={familyFilter} onChange={(event) => { updateQuery({ family: event.target.value }); }} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-800">
                <option value="all">全部体系</option>
                <option value="2.0">HSK 2.0</option>
                <option value="3.0">HSK 3.0</option>
                <option value="CSCA">CSCA</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-gray-600">
              <span>考试记录</span>
              <select aria-label="筛选考试记录" value={testsFilter} onChange={(event) => { updateQuery({ tests: event.target.value }); }} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-800">
                <option value="all">全部学生</option>
                <option value="has">有记录</option>
                <option value="none">无记录</option>
              </select>
            </label>
            {isAdmin && <>
              <label className="flex flex-col gap-1 text-gray-600">
                <span>分配教师</span>
                <select aria-label="筛选分配教师" value={teacherFilter} onChange={(event) => { updateQuery({ teacher: event.target.value }); }} className="max-w-44 rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-800">
                  <option value="all">全部教师</option>
                  <option value="unassigned">未分配</option>
                  {teachers.map((teacher) => <option key={teacher.id} value={teacher.id}>{teacher.username}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-gray-600">
                <span>教师权限</span>
                <select aria-label="筛选教师权限" value={accessFilter} onChange={(event) => { updateQuery({ access: event.target.value }); }} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-800">
                  <option value="all">全部状态</option>
                  <option value="approved">已批准</option>
                  <option value="pending">未批准</option>
                </select>
              </label>
            </>}
            <label className="flex flex-col gap-1 text-gray-600">
              <span>排序依据</span>
              <select aria-label="排序依据" value={sortBy} onChange={(event) => { updateQuery({ sort: event.target.value }); }} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-800">
                <option value="tests">考试次数</option>
                <option value="name">学生姓名</option>
                <option value="joined">注册时间</option>
              </select>
            </label>
            <button type="button" onClick={() => { updateQuery({ direction: direction === 'desc' ? 'asc' : 'desc' }); }} aria-label={direction === 'desc' ? '切换为升序' : '切换为降序'} className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 font-medium text-gray-700 hover:bg-gray-50">
              {direction === 'desc' ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
              {direction === 'desc' ? '降序' : '升序'}
            </button>
            {(familyFilter !== 'all' || testsFilter !== 'all' || teacherFilter !== 'all' || accessFilter !== 'all') &&
              <button type="button" onClick={() => { updateQuery({ family: 'all', tests: 'all', teacher: 'all', access: 'all' }); }} className="rounded-md px-2 py-2 text-indigo-700 hover:bg-indigo-50">清除筛选</button>}
          </div>
          {loading && hasLoaded && <p role="status" className="mt-3 text-sm text-gray-500">正在更新学生列表…</p>}
          {error && <div role="alert" className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          {teacherLoadError && isAdmin && (
            <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              {teacherLoadError}
            </div>
          )}
        </div>
        <div className="overflow-x-auto" aria-busy={loading}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tests Completed
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  学习体系 / 最近成绩
                </th>
                {isAdmin && (
                  <>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Teacher Access
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned Teacher
                    </th>
                  </>
                )}
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 6 : 4} className="px-6 py-4 text-center text-sm text-gray-500">
                    没有符合条件的学生。
                  </td>
                </tr>
              ) : (
                users.map((user) => {

                  return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="max-w-56 text-sm font-medium text-gray-900 break-words">{user.username}</div>
                      {isAdmin && <div className="mt-1 max-w-56 break-all text-xs text-gray-500" title={user.email}>{user.email}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.test_count}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-2 text-xs">
                        {user.learningError && <span className="text-amber-700">学习情况读取失败，请进入 Detail 重试</span>}
                        {user.learning && historyAccess(user.learning.restrictions).families.map(family => {
                          const latest = user.learning!.summaries.filter(r => r.family === family).sort((a,b) => b.latest_at.localeCompare(a.latest_at))[0];
                          return <div key={family}><b className="text-indigo-700">{historyFamilyLabel(family)}</b>{latest ? <><span className="ml-2">{historyCategoryLabel(latest.category)} · {latest.score == null || !latest.max_score ? '暂未计分' : latest.workbook ? Math.round(latest.score/latest.max_score*100)+'%' : Number(latest.score.toFixed(1))+' / '+latest.max_score}</span><span className="block text-slate-400">{family === 'CSCA' ? getCscaSubjectName(latest.level) : `HSK ${latest.level}级`}</span></> : <span className="ml-2 text-slate-400">暂无记录</span>}</div>;
                        })}
                      </div>
                    </td>
                    {isAdmin && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => setTeacherAccess(user.id, !user.teacher_access_approved)}
                            disabled={savingUserId === user.id}
                            className={`px-3 py-1 rounded text-xs font-medium ${
                              user.teacher_access_approved
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } ${savingUserId === user.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {user.teacher_access_approved ? 'Approved' : 'Approve'}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            aria-label={`为 ${user.username} 分配教师`}
                            value={user.assigned_teacher_id || ''}
                            onChange={(e) => setTeacherAssignment(user.id, e.target.value || null)}
                            disabled={savingUserId === user.id || teachers.length === 0}
                            className="border rounded px-2 py-1 text-sm text-gray-700"
                          >
                            <option value="">Unassigned</option>
                            {teachers.map((teacher) => (
                              <option key={teacher.id} value={teacher.id}>
                                {teacher.username}
                              </option>
                            ))}
                          </select>
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => navigate(`/admin/users/${user.id}`, { state: { user, returnTo: location.pathname + location.search } })}
                        className="text-primary-600 hover:text-primary-900 flex items-center justify-end ml-auto"
                      >
                        Details
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-4 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span>
            {total === 0 ? 'No students' : `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)} of ${total}`}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={loading || page <= 1}
              className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={loading || page >= totalPages}
              className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
